import { jwtDecode } from 'jwt-decode';

import { acceptDisclaimer as acceptDisclaimerApi } from './api/acceptDisclaimer';
import { changePassword as changePasswordApi } from './api/changePassword';
import { login as loginApi } from './api/login';
import { getLoginMode as getLoginModeApi } from './api/loginMode';
import { refreshAuthToken as refreshAuthTokenApi } from './api/refreshAuthToken';
import { resetPassword as resetPasswordApi } from './api/resetPassword';
import { AbstractStringStore } from './storage/AbstractStringStore';
import { CookieStore } from './storage/CookieStore';
import { LocalStorageStore } from './storage/LocalStorageStore';
import type { AccessToken } from './auth';
import { PortalApiConfigurationMissingError } from './error';
import { createPortalApiClient, PortalApiClient } from './portalApiClient';
import { asArray, getFirstValue } from './util';

type PortalApiConfig = {
  apiId: string;
  baseUrl: string;
};

type ClearMode = 'EXCEPT_REDIRECT_URL' | 'ONLY_REDIRECT_URL';

export type SessionConfig = {
  /**
   * The store key(s) for the JWT auth token. If an array is given, a token will be searched by iterating all keys
   * until a store entry is found. When a new auth token is stored, only the first item of the array is used as a
   * store key, the others are for retrieval only.
   */
  authTokenKey: string | string[];
  portalApi?: PortalApiConfig;
  /**
   * Store key to save a redirect URL that should be navigated to after the login.
   */
  redirectUrlKey: string;
  /**
   * The store key(s) for the JWT refresh token. If an array is given, a token will be searched by iterating all keys
   * until a store entry is found. When a new refresh token is stored, only the first item of the array is used as a
   * store key, the others are for retrieval only.
   */
  refreshTokenKey: string | string[];
  /**
   * List of stores to search for auth/refresh tokens. When storing new tokens they are only stored in the store at the
   * first position of the given array.
   */
  stores: AbstractStringStore[];
};

export class Session {
  #decodedAuthToken: AccessToken | undefined;

  readonly #primaryStore: AbstractStringStore;

  readonly config: SessionConfig;
  readonly #defaultConfig: SessionConfig = {
    authTokenKey: 'auth_token',
    redirectUrlKey: 'redirect_url',
    refreshTokenKey: 'refresh_token',
    stores: [
      new LocalStorageStore(),
      new CookieStore({
        secure: window.location.protocol === 'https:',
      }),
    ],
  };

  readonly #portalApiClient?: PortalApiClient;

  constructor(config: Partial<SessionConfig> = {}) {
    this.config = {
      ...this.#defaultConfig,
      ...config,
    };

    this.#primaryStore = this.config.stores[0];

    if (this.config.portalApi) {
      this.#portalApiClient = createPortalApiClient({
        ...this.config.portalApi,
        session: this,
      });
    }
  }

  protected getDynamicData(key: string) {
    return this.#primaryStore.retrieve(key);
  }

  protected removeDynamicData(key: string) {
    return this.#primaryStore.remove(key);
  }

  protected setDynamicData(key: string, value: string) {
    this.#primaryStore.store(key, value);
  }

  protected setHasToChangePassword = (hasToChangePassword: boolean) =>
    this.setDynamicData('hasToChangePassword', String(hasToChangePassword));

  async getLoginMode(email?: string, abortSignal?: AbortSignal) {
    return await getLoginModeApi(
      email ?? null,
      abortSignal ?? null,
      this.getPortalApiClientOrThrow(
        'Was not able to request login mode as api client was not ready'
      )
    );
  }

  async acceptDisclaimer(version: number) {
    await acceptDisclaimerApi(
      version,
      this.getPortalApiClientOrThrow(
        'Was not able to login as api client was not ready'
      )
    );
  }

  async login(email: string, password: string): Promise<void> {
    const result = await loginApi(
      email,
      password,
      this.getPortalApiClientOrThrow(
        'Was not able to login as api client was not ready'
      )
    );
    this.setAuthToken(result.token);
    this.setRefreshToken(result.refresh_token);
    this.setHasToChangePassword(result.needsPasswordChange);
  }

  async refreshAuthToken() {
    const refreshToken = this.getRefreshToken();
    if (refreshToken == null) {
      throw new Error('Cannot refresh auth token without refresh token set.');
    }
    const res = await refreshAuthTokenApi(this);
    this.setAuthToken(res.token);
  }

  async resetPassword(email: string): Promise<void> {
    await resetPasswordApi(
      email,
      this.getPortalApiClientOrThrow(
        'Was not able to reset password as api client was not ready'
      )
    );
  }

  async changePassword(newPassword: string): Promise<void> {
    const userId = this.getUserId();
    if (userId == null) {
      throw new Error('Cannot determine userId for password change');
    }

    await changePasswordApi(
      newPassword,
      userId,
      this.getPortalApiClientOrThrow(
        'Was not able to change password as api client was not ready'
      )
    );
    this.setHasToChangePassword(false);
  }

  /**
   * @returns true if logout was successful (or not necessary)
   */
  async logout(): Promise<boolean> {
    if (!this.#portalApiClient) {
      throw new PortalApiConfigurationMissingError();
    }

    const accessToken = this.getAuthToken();
    const refreshToken = this.getRefreshToken();

    if (accessToken && refreshToken) {
      const { response } = await this.#portalApiClient.untyped.POST(
        '/api/v1/logout',
        {
          body: {
            token: accessToken,
            refresh_token: refreshToken,
          },
        }
      );

      this.clear();
      return response.ok;
    }

    this.clear();
    return true;
  }

  clear(mode: ClearMode = 'EXCEPT_REDIRECT_URL'): void {
    if (mode === 'ONLY_REDIRECT_URL') {
      for (const store of this.config.stores) {
        store.remove(this.config.redirectUrlKey);
      }
    } else {
      // It's easier to store the redict url in a temporary variable, clear the complete store and save it back
      const redirectUrl = this.getRedirectUrl();

      this.#decodedAuthToken = undefined;

      for (const store of this.config.stores) {
        store.clear();

        // Make sure auth and refresh token are remove explicitly, because they might be set by third party systems
        // (like Keycloak SSO) and are not registered in the portal-auth store.
        asArray(this.config.authTokenKey)
          .concat(asArray(this.config.refreshTokenKey))
          .forEach((key) => store.remove(key));
      }

      if (redirectUrl) {
        this.setRedirectUrl(redirectUrl);
      }
    }
  }

  getPortalApiClient(): PortalApiClient | undefined {
    return this.#portalApiClient;
  }

  getPortalApiClientOrThrow(message: string): PortalApiClient {
    if (this.#portalApiClient == null) {
      throw new Error(message);
    }
    return this.#portalApiClient;
  }

  getAuthToken(): string | undefined {
    for (const store of this.config.stores) {
      const token = getFirstValue(asArray(this.config.authTokenKey), (key) =>
        store.retrieve(key)
      );

      if (token) {
        return token;
      }
    }

    return;
  }

  getDecodedAuthToken(): AccessToken | undefined {
    return this.#decodedAuthToken;
  }

  getRedirectUrl(opts?: { clear?: boolean }): string | undefined {
    const redirectUrl = this.#primaryStore.retrieve(this.config.redirectUrlKey);

    if (opts?.clear === true) {
      this.clear('ONLY_REDIRECT_URL');
    }

    return redirectUrl;
  }

  getRefreshToken(): string | undefined {
    for (const store of this.config.stores) {
      const token = getFirstValue(asArray(this.config.refreshTokenKey), (key) =>
        store.retrieve(key)
      );

      if (token) {
        return token;
      }
    }

    return;
  }

  setAuthToken(token: string): void {
    const storeKey = Array.isArray(this.config.authTokenKey)
      ? this.config.authTokenKey[0]
      : this.config.authTokenKey;

    this.#primaryStore.store(storeKey, token);
    this.#decodedAuthToken = jwtDecode<AccessToken>(token);
  }

  setRedirectUrl(url: string): void {
    this.#primaryStore.store(this.config.redirectUrlKey, url);
  }

  setRefreshToken(token: string): void {
    const storeKey = Array.isArray(this.config.refreshTokenKey)
      ? this.config.refreshTokenKey[0]
      : this.config.refreshTokenKey;

    this.#primaryStore.store(storeKey, token);
  }

  isAuthTokenExpired = (): boolean => {
    const tokenData = this.#decodedAuthToken;
    if (!tokenData) return true;

    return tokenData.exp * 1000 < Date.now();
  };

  getUserId = (): number | undefined => {
    return this.getDecodedAuthToken()?.identity.id;
  };

  getUserEmail = (): string | undefined => {
    return this.getDecodedAuthToken()?.identity.email;
  };

  hasToChangePassword = (): boolean =>
    this.getDynamicData('hasToChangePassword') === 'true';

  hasScope = (scope: string): boolean => {
    const decodedAuthToken = this.getDecodedAuthToken();
    if (!decodedAuthToken) {
      return false;
    }
    return decodedAuthToken.identity?.scopes?.includes(scope) ?? false;
  };
}

export default Session;
